import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Building2, Hammer, Droplets, ClipboardCheck, FileText, Package, Shield, Award, Users, Lightbulb, Leaf } from 'lucide-react';
import axios from '../config/axios';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const heroSlides = [
    {
      image: '/images/building-constraction.jpeg',
      title: 'BUILDING EXCELLENCE',
      subtitle: 'Transforming Visions Into Reality',
      description: 'From commercial complexes to residential developments, we deliver construction projects that stand the test of time with unmatched quality and precision.'
    },
    {
      image: '/images/Civil-raod.jpeg',
      title: 'INFRASTRUCTURE DEVELOPMENT',
      subtitle: 'Connecting Communities, Building Progress',
      description: 'Expert civil works and road infrastructure solutions that create lasting connections and drive economic growth across Kenya.'
    },
    {
      image: '/images/water-infrastructure.jpeg',
      title: 'SUSTAINABLE SOLUTIONS',
      subtitle: 'Engineering Tomorrow, Today',
      description: 'Innovative water infrastructure and sustainable development projects that ensure reliable resources for future generations.'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchFeaturedProjects();
  }, []);

  const fetchFeaturedProjects = async () => {
    try {
      const response = await axios.get('/projects?featured=1');
      if (response.data.success) {
        setFeaturedProjects(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };
  const capabilities = [
    {
      number: '01',
      title: 'Building & Construction',
      description: 'From commercial and residential developments to specialized facilities, we deliver construction solutions focused on quality, functionality and durability.',
      icon: Building2,
      link: '/services#building'
    },
    {
      number: '02',
      title: 'Civil & Road Works',
      description: 'We undertake civil and road infrastructure works, including earthworks, road improvements, gravelling and associated infrastructure development.',
      icon: Hammer,
      link: '/services#civil'
    },
    {
      number: '03',
      title: 'Water & Infrastructure',
      description: 'Our experience includes water infrastructure projects such as dams, boreholes, water storage systems and water collection facilities.',
      icon: Droplets,
      link: '/services#water'
    },
    {
      number: '04',
      title: 'Project & Construction Management',
      description: 'We coordinate projects from planning and pre-construction through execution and completion, helping clients manage time, cost, quality and project coordination.',
      icon: ClipboardCheck,
      link: '/services#project'
    },
    {
      number: '05',
      title: 'Consultancy & Design Management',
      description: 'We provide professional support in design review, value engineering, cost analysis, tender management, claims and project troubleshooting.',
      icon: FileText,
      link: '/services#consultancy'
    },
    {
      number: '06',
      title: 'Supplies & Procurement',
      description: 'We support clients with procurement and supply solutions, including sourcing, evaluation and delivery of required materials, equipment and specialized supplies.',
      icon: Package,
      link: '/services#supplies'
    }
  ];

  const whyChoose = [
    {
      icon: Users,
      title: 'Experienced Team',
      description: 'Our projects are supported by experienced technical and construction professionals with practical knowledge across different project environments.'
    },
    {
      icon: Award,
      title: 'Quality Focus',
      description: 'We maintain a strong commitment to quality workmanship, materials and project outcomes.'
    },
    {
      icon: Building2,
      title: 'Integrated Approach',
      description: 'We bring construction, project management, technical coordination and procurement capabilities together under one approach.'
    },
    {
      icon: Shield,
      title: 'Safety First',
      description: 'Safety is an important part of how we plan and execute our projects.'
    },
    {
      icon: Lightbulb,
      title: 'Client Focus',
      description: 'We work closely with clients and project stakeholders to understand requirements and deliver solutions aligned with project objectives.'
    }
  ];

  const approach = [
    {
      number: '01',
      title: 'Understand',
      description: 'We understand the client\'s objectives, requirements and project expectations.'
    },
    {
      number: '02',
      title: 'Plan',
      description: 'We develop an appropriate project approach, coordinate resources and establish delivery priorities.'
    },
    {
      number: '03',
      title: 'Coordinate',
      description: 'We coordinate consultants, contractors, suppliers and project stakeholders.'
    },
    {
      number: '04',
      title: 'Build',
      description: 'We execute and manage construction activities with focus on quality, safety and efficiency.'
    },
    {
      number: '05',
      title: 'Deliver',
      description: 'We work toward successful completion, handover and client satisfaction.'
    }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We conduct our work with honesty, transparency and accountability.'
    },
    {
      icon: Award,
      title: 'Quality',
      description: 'We strive for high standards in workmanship, materials and project delivery.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We embrace practical technologies and improved approaches to project delivery.'
    },
    {
      icon: Shield,
      title: 'Safety',
      description: 'We prioritize safe working environments for our teams, clients and the public.'
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'We seek responsible construction practices that consider long-term environmental impact.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Home - BURALE Maintainance & Suppliers | Construction & Project Management</title>
        <meta name="description" content="BURALE Maintainance and Suppliers Limited delivers professional construction, civil works, infrastructure, project management and supply solutions." />
      </Helmet>
      
      <section className="relative h-screen overflow-hidden">
        {/* Carousel Slides */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            ></div>
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
            
            {/* Content */}
            <div className="relative z-10 h-full flex items-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="max-w-3xl">
                  <div className="mb-6 flex items-center space-x-3">
                    <div className="h-1 w-16 bg-burale-gold"></div>
                    <span className="text-burale-gold font-semibold tracking-wider">BURALE MAINTAINANCE & SUPPLIERS</span>
                  </div>
                  
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                    {slide.title}
                  </h1>
                  
                  <p className="text-2xl md:text-3xl text-burale-gold mb-6 font-semibold">
                    {slide.subtitle}
                  </p>
                  
                  <p className="text-lg md:text-xl text-gray-200 mb-10 leading-relaxed">
                    {slide.description}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link 
                      to="/services" 
                      className="bg-burale-teal text-white px-8 py-4 rounded-lg font-semibold hover:bg-burale-teal/90 transition-all duration-300 inline-flex items-center justify-center text-lg shadow-lg hover:shadow-xl"
                    >
                      Explore Our Services <ArrowRight className="ml-2" size={20} />
                    </Link>
                    <Link 
                      to="/projects" 
                      className="bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-burale-dark transition-all duration-300 inline-flex items-center justify-center text-lg"
                    >
                      View Our Projects <ArrowRight className="ml-2" size={20} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'w-12 bg-burale-gold' 
                  : 'w-2 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>

        {/* Slide Counter */}
        <div className="absolute bottom-8 right-8 z-20 text-white font-semibold">
          <span className="text-2xl">{String(currentSlide + 1).padStart(2, '0')}</span>
          <span className="text-gray-400 mx-2">/</span>
          <span className="text-gray-400">{String(heroSlides.length).padStart(2, '0')}</span>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-burale-teal font-semibold text-sm uppercase tracking-wider mb-2">WHO WE ARE</p>
            <h2 className="section-heading">Building with Purpose. Delivering with Confidence.</h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Burale Maintainance and Suppliers Limited is a construction and project management company dedicated to delivering high-quality, innovative and sustainable solutions. Our capabilities span building construction, civil works, road and water infrastructure, electrical works, project management, consultancy and supplies.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              We combine technical expertise, experienced teams and practical project management approaches to help clients transform their ideas into successful projects.
            </p>
            <div className="text-center">
              <Link to="/about" className="btn-secondary inline-flex items-center">
                More About Us <ArrowRight className="ml-2" size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-burale-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-heading">What We Do</h2>
            <p className="section-subheading">Integrated solutions for construction, infrastructure and project delivery.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((capability) => {
              const Icon = capability.icon;
              return (
                <div key={capability.number} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group">
                  <div className="flex items-center mb-4">
                    <span className="text-5xl font-bold text-burale-gold mr-4">{capability.number}</span>
                    <Icon className="text-burale-teal" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-burale-dark mb-4">{capability.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{capability.description}</p>
                  <Link to={capability.link} className="text-burale-teal font-semibold inline-flex items-center group-hover:translate-x-2 transition-transform duration-300">
                    Learn More <ArrowRight className="ml-2" size={18} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-heading">Why Choose Burale?</h2>
            <p className="section-subheading">Experience, professionalism and a commitment to delivering value.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChoose.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center p-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-burale-teal/10 rounded-full mb-6">
                    <Icon className="text-burale-teal" size={40} />
                  </div>
                  <h3 className="text-xl font-bold text-burale-dark mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-burale-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">From Concept to Completion</h2>
            <p className="text-xl text-gray-300">A structured approach to delivering successful projects.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {approach.map((step, index) => (
              <div key={step.number} className="relative">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-burale-teal rounded-full mb-4">
                    <span className="text-2xl font-bold">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-burale-gold">{step.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
                </div>
                {index < approach.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-burale-teal/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-heading">Our Experience</h2>
            <p className="section-subheading">Delivering projects across buildings, infrastructure, roads and water development.</p>
          </div>
          {loadingProjects ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-burale-teal"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {featuredProjects.length === 0 ? (
                <div className="col-span-2 text-center py-12 text-gray-500">
                  No featured projects available
                </div>
              ) : (
                featuredProjects.map((project) => (
                  <div key={project.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
                    <div className="h-64 bg-gray-100 overflow-hidden">
                      {project.image ? (
                        <img 
                          src={project.image} 
                          alt={project.title}
                          className="w-full h-full object-cover object-center"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-burale-teal to-burale-dark">
                          <Building2 className="text-white/20" size={120} />
                        </div>
                      )}
                    </div>
                    <div className="p-8">
                      <span className="text-burale-teal font-semibold text-sm uppercase tracking-wider">{project.category}</span>
                      <h3 className="text-2xl font-bold text-burale-dark mt-2 mb-4">{project.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{project.description}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          <div className="text-center">
            <Link to="/projects" className="btn-primary inline-flex items-center text-lg">
              View All Projects <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-burale-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-heading">What We Stand For</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-6 shadow-lg">
                    <Icon className="text-burale-teal" size={48} />
                  </div>
                  <h3 className="text-xl font-bold text-burale-dark mb-3">{value.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-burale-teal text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Have a Project in Mind?</h2>
          <p className="text-xl mb-8 leading-relaxed">
            Whether you are planning a new development, infrastructure project, renovation or specialized construction project, our team is ready to discuss your requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-burale-teal px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center text-lg">
              Talk to Our Team <ArrowRight className="ml-2" size={20} />
            </Link>
            <Link to="/services" className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-burale-teal transition-all duration-300 inline-flex items-center justify-center text-lg">
              Explore Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
